<script>
  import { onDestroy, onMount } from "svelte";
  import {
    callQueue,
    dataInfo,
    activeSpec,
    unlockedSpecs,
    currentSpec,
    directContext,
    menuConfig,
    currMenu,
    currentElementLockGroup,
  } from "../../editor/storage.js";
  import { firstLevelMenus } from "../../editor/menuSchema.js";
  import { deepcopy, simpleNotUndefinedEq } from "../../editor/utility.js";
  import AxisEditor from "../component/axisEditor.svelte";
  import {} from "../../editor/direct.js";
  import { findAxis, unfindAxis } from "../../editor/component/axis.js";

  // event functions
  export let inputFocusAction;
  export let inputBlurAction;

  let subMenu;
  let currAxes;
  let isSerializable = false;

  function setValues() {
    currAxes = [];
    $currentSpec.fronto?.layout?.row?.forEach((rc, i, rcArray) => {
      currAxes.push(getRCDesign(rc, i, rcArray, "row"));
    });
    $currentSpec.fronto?.layout?.column?.forEach((rc, i, rcArray) => {
      currAxes.push(getRCDesign(rc, i, rcArray, "column"));
    });
    if (
      $currentSpec.fronto?.layer?.length == 1 &&
      ["bar", "square", "image", "rect"].includes(
        $currentSpec.fronto.layer[0].mark?.type
      )
    ) {
      isSerializable = true;
    } else {
      isSerializable = false;
    }
  }
  function getRCDesign(rc, index, rcArray, role) {
    let elem = { role, index };
    // field/aggregate
    if (rc.field) elem.field = rc.field;
    else if (rc.aggregate === "count") elem.aggregate = "count";
    // axis design -- last axis (vaxis)
    if (index == rcArray.length - 1) {
      if (
        elem.role === "row" &&
        $currentSpec.fronto.layout.vaxis !== undefined
      ) {
        elem.axisDesign = deepcopy($currentSpec.fronto.layout.vaxis);
        elem.subrole = { row: "y", column: "x" }[elem.role];
      } else if (
        elem.role === "column" &&
        $currentSpec.fronto.layout.haxis !== undefined
      ) {
        elem.axisDesign = deepcopy($currentSpec.fronto.layout.haxis);
        elem.subrole = { row: "y", column: "x" }[elem.role];
      } else {
        elem.axisDesign = {};
        elem.subrole = elem.role;
      }
    }
    // text design
    elem.textDesign = [];
    $currentSpec.fronto?.layer?.forEach((layer, li) => {
      layer.text?.forEach((textElem, ti) => {
        if (
          textElem.type === "on-axis" &&
          (simpleNotUndefinedEq(textElem.field, rc.field) ||
            simpleNotUndefinedEq(textElem.aggregate, rc.aggregate))
        ) {
          if (textElem.lock) elem.textLock = textElem.lock;
          let textDesign = deepcopy(textElem);
          textDesign.layerIndex = li;
          textDesign.textIndex = ti;
          elem.textDesign.push(textDesign);
          textDesign.items?.forEach((item) => {
            if (item.custom) {
              item.customList = [];
              Object.keys(item.custom).forEach((c) => {
                item.customList.push({
                  value: c,
                  text: item.custom[c],
                });
              });
            }
          });
        }
      });
    });
    return elem;
  }
  function customListToObj(list) {
    let obj = {};
    list.forEach((c) => {
      obj[c.value] = c.text;
    });
    return obj;
  }
  let currAxisToEdit;
  function setSubMenu(s, i) {
    subMenu = s;
    currAxisToEdit = i;
  }
  function cancelSubMenu() {
    subMenu = undefined;
    currAxisToEdit = undefined;
  }

  function updateAxis(event, role, axisItem) {
    let newValue;
    if (role.endsWith("lock")) {
      updateLock(event, role, axisItem);
    } else if (
      [
        "axis.grid",
        "axis.domain",
        "axis.label",
        "axis.title",
        "axis.repeat",
      ].includes(role)
    ) {
      newValue = event.target.checked || null;
    } else if (
      ["axis.grid.color", "axis.domain.color", ""].includes(role) ||
      role.match(/^axis\.label\.[0-9]+\.(fill)$/gi)
    ) {
      newValue =
        "rgba(" +
        [event.detail.r, event.detail.g, event.detail.b, event.detail.a].join(
          ","
        ) +
        ")";
    } else if (
      [
        "axis.domain.width",
        "axis.grid.width",
        "axis.offset",
        "axis.domain.opacity",
        "axis.grid.opacity",
      ].includes(role)
    ) {
      newValue = parseFloat(event.target.value);
    } else if (role === "axis.domain.dash" || role == "axis.grid.dash") {
      newValue = event.target.value.split(",").map((d) => parseFloat(d));
    } else if (role.match(/^axis\.label\.[0-9]+\.values$/gi)) {
      newValue = event.target.value.split(",");
      if ($dataInfo.stats[axisItem.field].rawType === "Number")
        newValue = newValue.map((d) => parseFloat(d.trim()));
    } else if (
      role.match(/^axis\.label\.[0-9]+\.(anchor|orient|dx|dy|flex)$/gi)
    ) {
      newValue = event.target.value;
      if (newValue === "undefined") newValue = null;
    } else if (role.match(/^axis\.label\.[0-9]+\.(serial)$/gi)) {
      newValue = event.target.checked;
      if (newValue === "undefined") newValue = null;
    } else if (role.match(/^axis\.label\.[0-9]+\.(padding|angle)$/gi)) {
      newValue = parseFloat(event.target.value);
    } else if (
      role.match(/^axis\.label\.[0-9]+\.items$/gi) ||
      role.match(/^axis\.label\.[0-9]+\.items\.[0-9]+$/gi)
    ) {
      newValue = event;
    } else if (
      role.match(
        /^axis\.label\.[0-9]+\.items\.[0-9]+\.(values|fontSize|width|color|opacity|fontWeight|align|format|expression|custom|lineHeight)$/gi
      )
    ) {
      if (role.endsWith("values")) {
        newValue = event.target.value.split(",");
        if ($dataInfo.stats[axisItem.field].rawType === "Number")
          newValue = newValue.map((d) => parseFloat(d.trim()));
      } else if (
        role.endsWith("fontSize") ||
        role.endsWith("width") ||
        role.endsWith("opacity") ||
        role.endsWith("lineHeight")
      ) {
        newValue = parseFloat(event.target.value) || null;
      } else if (
        role.endsWith("align") ||
        role.endsWith("format") ||
        role.endsWith("expression") ||
        role.endsWith("fontWeight")
      ) {
        newValue = event.target.value;
        if (newValue.length == 0) {
          newValue = null;
        }
      } else if (role.endsWith("color")) {
        newValue =
          "rgba(" +
          [event.detail.r, event.detail.g, event.detail.b, event.detail.a].join(
            ","
          ) +
          ")";
      } else if (role.endsWith("custom")) {
        let axisTextIndex = parseInt(role.match(/[0-9]+/gi)[0]);
        let axisTextItemIndex = parseInt(role.match(/[0-9]+/gi)[1]);
        newValue = customListToObj(
          event?.constructor.name === "Array"
            ? event
            : axisItem.customList || []
        );
        if (event.newCustomValue !== undefined) {
          newValue[event.newCustomValue] = event.newCustomText;
        }
      }
    } else if (role.match(/^axis\.label\.[0-9]+\.tick$/gi)) {
      // use tick
      newValue = event.target.checked || null;
    } else if (
      role.match(
        /^axis\.label\.[0-9]+\.tick\.(color|width|opacity|tickValues)$/gi
      )
    ) {
      // tick details
      if (role.endsWith("color")) {
        newValue =
          "rgba(" +
          [event.detail.r, event.detail.g, event.detail.b, event.detail.a].join(
            ","
          ) +
          ")";
      } else if (role.endsWith("width") || role.endsWith("opacity")) {
        newValue = parseFloat(event.target.value);
      } else if (role.endsWith("tickValues")) {
        newValue = event.target.value.split(",");
        if ($dataInfo.stats[axisItem.field].rawType === "Number")
          newValue = newValue.map((d) => parseFloat(d.trim()));
      }
    } else if (
      role.match(
        /^axis\.title\.(text|textAlign|color|fontSize|fontWeight|opacity|rotate|x|y|padding)$/gi
      )
    ) {
      if (
        role.endsWith("fontSize") ||
        role.endsWith("rotate") ||
        role.endsWith("opacity") ||
        role.endsWith("x") ||
        role.endsWith("y") ||
        role.endsWith("padding")
      ) {
        newValue = parseFloat(event.target.value) || null;
      } else if (
        role.endsWith("textAlign") ||
        role.endsWith("text") ||
        role.endsWith("fontWeight")
      ) {
        newValue = event.target.value;
        if (newValue.length == 0) {
          newValue = null;
        }
      } else if (role.endsWith("color")) {
        newValue =
          "rgba(" +
          [event.detail.r, event.detail.g, event.detail.b, event.detail.a].join(
            ","
          ) +
          ")";
      }
    }

    if (newValue !== undefined) {
      let rule = {
        description: "Edit axis ",
        specifier: { field: axisItem.field },
      };
      if (role.startsWith("axis.")) {
        rule.specifier.role = axisItem.role === "column" ? "hAxis" : "vAxis";
      }
      if (role.startsWith("axis.domain")) {
        rule.action = "modify";
        rule.description += "domain ";
        if (role === "axis.domain") {
          rule.description += newValue ? "(set)" : "(unset";
          rule.option = { domain: newValue };
        } else if (role == "axis.domain.color") {
          rule.description += "color";
          rule.option = { domain: { stroke: newValue } };
        } else if (role == "axis.domain.width") {
          rule.description += "width";
          rule.option = { domain: { strokeWidth: newValue } };
        } else if (role == "axis.domain.opacity") {
          rule.description += "opacity";
          rule.option = { domain: { strokeOpacity: newValue } };
        } else if (role == "axis.domain.dash") {
          rule.description += "dash";
          rule.option = { domain: { strokeDash: newValue } };
        }
      } else if (role === "axis.offset") {
        rule.action = "modify";
        rule.description += "offset from chart";
        rule.option = { offset: newValue };
      } else if (role === "axis.flex") {
        rule.action = "modify";
        rule.description += "label alignment";
        rule.option = { flex: newValue };
      } else if (role.startsWith("axis.grid")) {
        rule.action = "modify";
        rule.description += "grid ";
        if (role === "axis.grid") {
          rule.description += newValue ? "(set)" : "(unset";
          rule.option = { grid: newValue };
        } else if (role == "axis.grid.color") {
          rule.description += "color";
          rule.option = { grid: { stroke: newValue } };
        } else if (role == "axis.grid.width") {
          rule.description += "width";
          rule.option = { grid: { strokeWidth: newValue } };
        } else if (role == "axis.grid.opacity") {
          rule.description += "opacity";
          rule.option = { grid: { strokeOpacity: newValue } };
        } else if (role == "axis.grid.dash") {
          rule.description += "dash";
          rule.option = { grid: { strokeDash: newValue } };
        }
      } else if (role === "axis.label") {
        if (newValue != null) {
          rule.action = "add";
          rule.description += "labels (set)";
          rule.option = {
            role: "label",
          };
        } else {
          rule.specifier.role = rule.specifier.role + ".label";
          rule.action = "remove";
          rule.description += "labels (unset)";
        }
      } else if (role === "axis.repeat") {
        rule.action = "modify";
        rule.description += "repeat";
        rule.option = { repeat: newValue };
      } else if (role.startsWith("axis.label.")) {
        rule.action = "modify";
        rule.description += "label ";
        rule.specifier.role = rule.specifier.role + ".label";
        // todo: extend to multi-layer axis design
        // below: not used for now; maybe useful in the future
        // let axisTextIndex = parseInt(role.match(/[0-9]+$/gi)[0]);
        // let layerIndex = axisDesign.textDesign[axisTextIndex].layerIndex;
        // let textIndex =  axisDesign.textDesign[axisTextIndex].textIndex;
        if (role.match(/^axis\.label\.[0-9]+\.values$/gi)) {
          rule.description += "values";
          rule.option = { values: newValue };
        } else if (role.match(/^axis\.label\.[0-9]+\.anchor$/gi)) {
          rule.description += "anchor";
          rule.option = { anchor: newValue };
        } else if (role.match(/^axis\.label\.[0-9]+\.orient$/gi)) {
          rule.description += "orient";
          rule.option = { orient: newValue };
        } else if (role.match(/^axis\.label\.[0-9]+\.padding$/gi)) {
          rule.description += "padding";
          rule.option = { padding: newValue };
        } else if (role.match(/^axis\.label\.[0-9]+\.angle$/gi)) {
          rule.description += "angle";
          rule.option = { angle: newValue };
        } else if (role.match(/^axis\.label\.[0-9]+\.dx$/gi)) {
          rule.description += "dx";
          rule.option = { dx: newValue };
        } else if (role.match(/^axis\.label\.[0-9]+\.dy$/gi)) {
          rule.description += "dy";
          rule.option = { dy: newValue };
        } else if (role.match(/^axis\.label\.[0-9]+\.fill$/gi)) {
          rule.description += "fill";
          rule.option = { fill: newValue };
        } else if (role.match(/^axis\.label\.[0-9]+\.serial$/gi)) {
          rule.description += "serial";
          rule.option = { serial: newValue };
        } else if (role.match(/^axis\.label\.[0-9]+\.items$/gi)) {
          if (event === "add") {
            rule.action = "add";
            rule.description += "items (add)";
            rule.option = { items: [{ fontSize: 12 }] };
          }
        } else if (role.match(/^axis\.label\.[0-9]+\.items\.[0-9]+$/gi)) {
          let axisTextIndex = parseInt(role.match(/[0-9]+$/gi)[0]);
          if (event === "remove") {
            rule.action = "remove";
            rule.description += "items (delete)";
            rule.option = { items: { index: axisTextIndex } };
          }
        } else if (
          role.match(
            /^axis\.label\.[0-9]+\.items\.[0-9]+\.(values|fontSize|width|color|opacity|fontWeight|align|format|expression|custom|lineHeight)$/gi
          )
        ) {
          let axisTextIndex = parseInt(role.match(/[0-9]+/gi)[0]);
          let axisTextItemIndex = parseInt(role.match(/[0-9]+/gi)[1]);
          rule.description += "item (" + axisTextItemIndex + ") ";
          if (role.endsWith("values")) {
            rule.action = "modify";
            rule.description += "values";
            rule.option = {
              items: { index: axisTextItemIndex, values: newValue },
            };
          } else if (role.endsWith("fontSize")) {
            rule.action = "modify";
            rule.description += "font size";
            rule.option = {
              items: { index: axisTextItemIndex, fontSize: newValue },
            };
          } else if (role.endsWith("width")) {
            rule.action = "modify";
            rule.description += "width";
            rule.option = {
              items: { index: axisTextItemIndex, width: newValue },
            };
          } else if (role.endsWith("opacity")) {
            rule.action = "modify";
            rule.description += "opacity";
            rule.option = {
              items: { index: axisTextItemIndex, opacity: newValue },
            };
          } else if (role.endsWith("align")) {
            rule.action = "modify";
            rule.description += "align";
            rule.option = {
              items: { index: axisTextItemIndex, textAlign: newValue },
            };
          } else if (role.endsWith("format")) {
            rule.action = "modify";
            rule.description += "format";
            rule.option = {
              items: { index: axisTextItemIndex, format: newValue },
            };
          } else if (role.endsWith("expression")) {
            rule.action = "modify";
            rule.description += "expression";
            rule.option = {
              items: { index: axisTextItemIndex, expression: newValue },
            };
          } else if (role.endsWith("color")) {
            rule.action = "modify";
            rule.description += "color";
            rule.option = {
              items: { index: axisTextItemIndex, color: newValue },
            };
          } else if (role.endsWith("custom")) {
            rule.action = "modify";
            rule.description += "custom";
            rule.option = {
              items: { index: axisTextItemIndex, custom: newValue },
            };
          } else if (role.endsWith("fontWeight")) {
            rule.action = "modify";
            rule.description += "fontWeight";
            rule.option = {
              items: { index: axisTextItemIndex, fontWeight: newValue },
            };
          } else if (role.endsWith("lineHeight")) {
            rule.action = "modify";
            rule.description += "lineHeight";
            rule.option = {
              items: { index: axisTextItemIndex, lineHeight: newValue },
            };
          }
        } else if (role.match(/^axis\.label\.[0-9]+\.tick$/gi)) {
          // use tick
          if (newValue) {
            rule.action = "add";
            rule.specifier.role = rule.specifier.role.replace(".label", "");
            rule.description += "tick (set)";
            rule.option = { role: "tick", stroke: "black" };
          } else if (newValue == null) {
            rule.description += "tick (unset)";
            rule.action = "remove";
            rule.specifier.role = rule.specifier.role.replace(
              ".label",
              ".tick"
            );
          }
        } else if (
          role.match(
            /^axis\.label\.[0-9]+\.tick\.(color|width|opacity|tickValues)$/gi
          )
        ) {
          rule.action = "modify";
          rule.specifier.role = rule.specifier.role.replace(".label", ".tick");
          rule.description += "tick ";
          // tick details
          if (role.endsWith("color")) {
            rule.description += "color";
            rule.option = { stroke: newValue };
          } else if (role.endsWith("width")) {
            rule.description += "width";
            rule.option = { strokeWidth: newValue };
          } else if (role.endsWith("opacity")) {
            rule.description += "opacity";
            rule.option = { strokeOpacity: newValue };
          } else if (role.endsWith("tickValues")) {
            rule.description += "tick values";
            rule.option = { tickValues: newValue };
          }
        }
      } else if (role === "axis.title") {
        rule.action = "add";
        rule.description += "title ";
        if (role === "axis.title") {
          rule.description += newValue ? "(set)" : "(unset";
          rule.option = { role: "title" };
        }
      } else if (role.startsWith("axis.title.")) {
        rule.action = "modify";
        rule.description += "title ";
        if (role == "axis.title.fontSize") {
          rule.description += "font size";
          rule.option = { title: { fontSize: newValue } };
        } else if (role == "axis.title.rotate") {
          rule.description += "rotate";
          rule.option = { title: { rotate: newValue } };
        } else if (role == "axis.title.opacity") {
          rule.description += "opacity";
          rule.option = { title: { opacity: newValue } };
        } else if (role == "axis.title.x") {
          rule.description += "x";
          rule.option = { title: { x: newValue } };
        } else if (role == "axis.title.y") {
          rule.description += "y";
          rule.option = { title: { y: newValue } };
        } else if (role == "axis.title.padding") {
          rule.description += "padding";
          rule.option = { title: { padding: newValue } };
        } else if (role == "axis.title.textAlign") {
          rule.description += "text align";
          rule.option = { title: { textAlign: newValue } };
        } else if (role == "axis.title.text") {
          rule.description += "text";
          rule.option = { title: { text: newValue } };
        } else if (role == "axis.title.fontWeight") {
          rule.description += "font weight";
          rule.option = { title: { fontWeight: newValue } };
        } else if (role == "axis.title.color") {
          rule.description += "color";
          rule.option = { title: { fontColor: newValue } };
        }
      }
      if (rule) {
        rule.category = "manual";
        callQueue.update((queue) => {
          queue.add([rule], $unlockedSpecs).apply();
          return queue;
        });
      }
    }
  }

  // from external
  menuConfig.subscribe((config) => {
    if (config?.subMenu && config?.field) {
      setValues();
      let li = currAxes
        ?.map((d, i) => {
          return d.field == config.field ||
            (d.aggregate === config.field && config.field === "count")
            ? i
            : undefined;
        })
        .filter((d) => d !== undefined)[0];
      setSubMenu(config.subMenu, li);
    }
  });

  // pipelines
  onMount(() => {
    setValues();
  });
  currentSpec.subscribe(() => {
    setValues();
  });
  onDestroy(() => {
    cancelSubMenu();
  });

  function updateLock(event, role, axisItem) {
    if ($currentElementLockGroup.length > 0) {
      let newValue = event.target.checked;
      if (newValue !== undefined && role && axisItem) {
        let rule = {
          category: "lock",
          description: (newValue ? "Lock" : "Unlock") + " axis",
          specifier: {
            field: axisItem.field,
          },
          action: "lock",
          option: {},
        };
        if (role === "axis.lock") {
          rule.specifier.role = "axis";
          rule.option.axis = newValue;
        } else if (role === "axis.label.lock") {
          rule.specifier.role = "axis.label";
          rule.description += " label";
          rule.option.label = newValue;
        }

        if (rule) {
          callQueue.update((queue) => {
            queue.add([rule], $currentElementLockGroup).apply();
            return queue;
          });
        }
      }
    }
  }
</script>

<section class="second-level-menu">
  <h2>
    {firstLevelMenus["axis"]?.title || "Axis"}
  </h2>

  {#if $activeSpec == -1}
    <div class="dupo-menu-pane mb-2 warn-msg pt-2" style="margin-top: -0.5rem;">
      <div class="dupo-menu-pane-row">
        <p class="text-sm">Create or select an artboard!</p>
      </div>
    </div>
  {:else if !$unlockedSpecs.includes($currentSpec.uid)}
    <div class="dupo-menu-pane mb-2 warn-msg pt-2" style="margin-top: -0.5rem;">
      <div class="dupo-menu-pane-row">
        <p class="text-sm">Unlock this artboard first!</p>
      </div>
    </div>
  {:else if !subMenu}
    <div class="dupo-menu-pane mb-2">
      <div class="dupo-menu-pane-row">
        <p class="text-sm">Choose axis to edit.</p>
      </div>
    </div>
    <div class="pane-divider" />
    <!-- Axis list -->
    <div class="dupo-menu-pane mb-2">
      <div class="dupo-menu-pane-row my-2">
        <h3 class="h-sub"><i class="bi bi-rulers me-1" />Axes</h3>
      </div>
      {#each currAxes as axis, ai}
        <div class="dupo-menu-pane-form-row">
          <div
            class="menu-second-level menu-with-buttons"
            on:mouseover={() => {
              findAxis($currentSpec.uid, axis);
            }}
            on:focus={() => {
              findAxis($currentSpec.uid, axis);
            }}
            on:mouseout={() => {
              unfindAxis();
            }}
            on:blur={() => {
              unfindAxis();
            }}
          >
            <div class="text-truncate" style="user-select:none;">
              <!-- todo: hover interaction -->
              {{
                row: "Vertical axis",
                column: "Horizontal axis",
              }[axis.role]}
              <i class="bi bi-caret-right-fill" />{axis.field || axis.aggregate}
            </div>
            <div class="dupo-pane-list-button">
              <button
                type="button"
                on:click={() => {
                  setSubMenu("edit", ai);
                }}
                ><i
                  class="bi bi-pencil-fill"
                  alt="{`Edit ${
                    {
                      row: 'Vertical axis',
                      column: 'Horizontal axis',
                    }[axis.role]
                  } for ${axis.field || axis.aggregate}`}}"
                /></button
              >
            </div>
          </div>
        </div>
      {/each}
    </div>
  {:else if subMenu == "edit"}
    <!-- Managing Existing -->
    <div class="dupo-menu-pane">
      <div class="dupo-menu-pane-row">
        <h3 class="h-sub mb-2">
          {{
            row: "Vertical axis",
            column: "Horizontal axis",
          }[currAxes[currAxisToEdit].role]}: {currAxes[currAxisToEdit].field ||
            currAxes[currAxisToEdit].aggregate}
        </h3>
      </div>
    </div>
    <AxisEditor
      {inputFocusAction}
      {inputBlurAction}
      axisItem={currAxes[currAxisToEdit]}
      {currAxisToEdit}
      {updateAxis}
      {isSerializable}
    />
    <div class="pane-divider" />
    <div class="dupo-menu-pane pb-2">
      <div class="dupo-menu-pane-button-row">
        <button
          type="button"
          on:click={() => {
            if ($directContext) currMenu.set("selection");
            else {
              unfindAxis();
              cancelSubMenu();
            }
          }}><i class="bi bi-arrow-left me-1" />Go back</button
        >
      </div>
    </div>
  {/if}
</section>
